import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Select, TextField } from "@shapr3d/frontend-lib";
import { Controller, type FieldError, useForm } from "react-hook-form";
import { z } from "zod";

import useAccount from "../../hooks/useAccount";
import FilePicker from "../FilePicker/FilePicker";
import Heading from "../Heading/Heading";

import style from "./EduForm.module.css";

enum Role {
  Student = "STUDENT",
  Teacher = "TEACHER",
  FacultyMember = "FACULTY_MEMBER",
}
interface EduFormProps {
  submit: (data: EduFormInputs) => void;
  showStudentIDUpload: boolean;

  isSubmitting?: boolean;
  errorMessage?: string;
}
export interface EduFormInputs {
  role: Role;
  firstName: string;
  lastName: string;
  school: string;
  major: string;
  idPhotos: File[] | undefined;
}
const schema = z.object({
  role: z.nativeEnum(Role, { required_error: "Required field" }),
  firstName: z.string().nonempty("Required field"),
  lastName: z.string().nonempty("Required field"),
  school: z.string().nonempty("Required field"),
  major: z.string().nonempty("Required field"),
});

const idPhotoSchema = z.object({
  idPhotos: z.array(z.unknown()).nonempty("Please upload at least one image"),
});

const getValidationSchema = (showStudentIDUpload: boolean) => {
  if (showStudentIDUpload) {
    return schema.merge(idPhotoSchema);
  }
  return schema;
};

const EduForm = (props: EduFormProps) => {
  const { data: accountData } = useAccount();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EduFormInputs>({
    resolver: zodResolver(getValidationSchema(props.showStudentIDUpload)),
  });

  return (
    <form onSubmit={handleSubmit(props.submit)} noValidate>
      <div className={style.modalContainer}>
        <Heading type="h1" className={style.heading}>
          Request EDU license
        </Heading>
        <p>Fill out this form to get or renew your EDU license.</p>
        <Controller
          control={control}
          name="role"
          render={({ field: { onChange } }) => (
            <Select
              errorMessage={errors.role?.message}
              error={Boolean(errors.role)}
              disabled={props.isSubmitting}
              onChange={(event) => onChange(event.target.value)}
              required
              defaultValue={""}
            >
              <option value="" disabled>
                Please select your role
              </option>
              <option value={Role.Teacher}>Teacher</option>
              <option value={Role.Student}>Student</option>
              <option value={Role.FacultyMember}>Faculty member</option>
            </Select>
          )}
        />
        <TextField
          errorMessage={errors.firstName?.message}
          placeholder="First name"
          disabled={props.isSubmitting}
          error={Boolean(errors.firstName)}
          {...register("firstName")}
        />

        <TextField
          errorMessage={errors.lastName?.message}
          placeholder="Last name"
          disabled={props.isSubmitting}
          error={Boolean(errors.lastName)}
          {...register("lastName")}
        />

        <TextField
          errorMessage={errors.school?.message}
          placeholder="School name"
          disabled={props.isSubmitting}
          error={Boolean(errors.school)}
          {...register("school")}
        />

        <TextField
          errorMessage={errors.major?.message}
          placeholder="Major area of study"
          disabled={props.isSubmitting}
          error={Boolean(errors.major)}
          {...register("major")}
        />

        {props.showStudentIDUpload && (
          <>
            <Controller
              control={control}
              name="idPhotos"
              render={({ field: { onChange } }) => (
                <FilePicker
                  disabled={props.isSubmitting}
                  title={
                    accountData?.countryCode === "CN" ? (
                      <>
                        Please attach a copy of your <a href="https://my.chsi.com.cn">CHSI</a>
                      </>
                    ) : (
                      "Photo(s) of your school ID"
                    )
                  }
                  maxFiles={2}
                  maxFileSizeInMB={1}
                  acceptedFileExtensions={["JPG", "JPEG", "PNG", "GIF", "BMP", "SVG", "WebP"]}
                  onChange={onChange}
                />
              )}
            />
            {(errors.idPhotos as FieldError | undefined)?.message && (
              <span className={style.error}>
                {(errors.idPhotos as FieldError | undefined)?.message}
              </span>
            )}
          </>
        )}

        <Button type="submit" disabled={props.isSubmitting}>
          Send request
        </Button>
        {props.errorMessage && <span className={style.error}>{props.errorMessage}</span>}

        <div className={style.disclaimer}>
          By continuing, you indicate that you are currently a member of an accredited educational
          institution.
        </div>
        <div className={style.disclaimer}>
          IMPORTANT: If you already have a Pro trial running,{" "}
          <strong>you need to cancel it manually</strong>. <br />
          Otherwise, you&apos;ll be charged at the end of the trial regardless of your EDU license.
        </div>
      </div>
    </form>
  );
};

export default EduForm;
